import React, {useState, useEffect} from "react"
import _ from "lodash"
import Isvg from "react-inlinesvg"
// Components
import { WithTip } from 'components/tooltip/with-tip'
import ConfirmationModal from "components/modal/confirmation-modal"
// Data
import calcService from "data/calc"
import { CalcStatus } from "data/interface/calc"
import keyService, * as chord from "data/key"
import socketService, * as eventType from "data/socket"
import { propertyChannel } from "data/websocket/sharedChannel"
import propertyService, {Property} from "data/property"
import validationService from "data/validate"
import { getPropertyDetails } from "data/lib/property"
// Icons
import { CalcIcon, ValidationIcon } from "images/icons/header"
import { CloseIcon } from "images/icons/general"
// Styles
import { HeaderIcon_s } from "components/wrapper/header/header-styles"
import { PropertyCalcState_s, CalcProgressBar_s, CalcIcon_s, PropertyCalcStateIcon_s, CalcCancel_s, CalcValidationIcon_s } from "styles/calc-styles.ts"
import { ShortcutHint_s } from 'styles/shortcut-styles'

const CalcButton = () => {
  const [currentProp, setCurrentProp] = useState<Property|null>(null)
  const [percentComplete, setPercentComplete] = useState<number>(0)
  const [hintsVisible, setHintsVisible] = useState<boolean>(false)
  const [showPreCalcWarning, setPreCalcWarning] = useState<boolean>(false)
  let {calcDetail, calcStatus, errordetail, severe, severeFolder } = getPropertyDetails(currentProp)
  const validationMessage = 'This property has validation errors. Please fix validation errors before continuing. Click Ignore to continue with calculation or Cancel to stop calculation.'
  const severeMessage = `This property${severeFolder ? ' or its containing folder settings ' : ' '}has validation errors which cannot be disregarded. You must fix the highlighted validation errors before continuing.`
  const currentPropId = _.invoke(JSON, "parse", localStorage.getItem("property"))

  if (_.get(propertyChannel, 'profiler.profile.percentCompleted') > percentComplete) {
    setPercentComplete(_.get(propertyChannel, 'profiler.profile.percentCompleted'))
  }

  useEffect(() => {
    const propertySubscription = propertyService.stream().subscribe(data => {
      if(data.value.length) setCurrentProp(_.find(data.value, prop => prop.propertyid === JSON.parse(localStorage.getItem('property')!)))
    })

    const socketSubscription = socketService.stream().subscribe(data => {
      switch(true) {
        case data.eventType === eventType.CALC_STATUS:
          setPercentComplete(data.event.percentCompleted)
          break
        case data.eventType === eventType.CALC_FINISHED:
          setPercentComplete(0)
          break
        case data.eventType === eventType.PROPCHANNEL_CALC_PROPERTY_SYNC:
          if (data.event?.property?.propertyid !== currentProp?.propertyid) return
          setCurrentProp(data.event.property)
          break
        case data.eventType === eventType.PROPERTY_SELECTED:
          setCurrentProp(_.find(propertyService.value(), prop => prop.propertyid === data.event.propertyId))
          break
      }
    })

    const keySubscription = keyService.stream().subscribe(k => {
      switch(true) {
        case k.chord === chord.CTRL_ALT && k.down === true:
          setHintsVisible(true)
          break
        case k.chord === chord.CTRL_ALT && k.down === false:
          setHintsVisible(false)
          break
        case k.chord === chord.CTRL_ALT_C && k.down === true:
          startCalc()
          break
        case k.chord === chord.CTRL_ALT_W && k.down === true:
          showValidationErrors()
          break
      }
    })

    return function cleanup() {
      propertySubscription.unsubscribe()
      socketSubscription.unsubscribe()
      keySubscription.unsubscribe()
    }
  }, [currentProp])

  const calc = () => calcService.start("property", [+currentPropId])

  const startCalc = () => {
    if(errordetail || severeFolder) setPreCalcWarning(true)
    else calc()
  }

  const cancelCalc = () => calcService.cancel(currentPropId)

  // const showValidationErrors = () => validationService.displayValidationErrors({errordetail})
  const showValidationErrors = () => {
    const folderid = currentProp?.folderid
    function validationFilter(errors) {
      if (!_.keys(_.get(errors, 'errordetail.folder')).length) return errors
      const folder = _.chain(errors).get('errordetail.folder').pick(folderid).value()
      if (!folder) return errors
      return {
        ...errors,
        errordetail: {...errors.errordetail, folder},
      }
    }

    validationService.displayValidationErrors(errordetail, validationFilter)
  }

  let calcStateComponent: JSX.Element | null = <PropertyCalcState_s><PropertyCalcStateIcon_s notCalc>Not Calculated</PropertyCalcStateIcon_s></PropertyCalcState_s>

  let calcConfirmation: any = null
  if (showPreCalcWarning) {
    calcConfirmation = <ConfirmationModal
      show={showPreCalcWarning}
      title={'Validation Warning'}
      confirmText={'Ignore'}
      message={severe || severeFolder ? severeMessage : validationMessage}
      confirmFunc={() => { calc(); setPreCalcWarning(false) }}
      cancelFunc={() => setPreCalcWarning(false)}
      toggleShow={() => setPreCalcWarning(false)}
      confirmOnly={false}
      closeOnly={severe || severeFolder}/>
  }

  // these conditions are evalutated in this order; i.e. they are not independent
  const calculatedWithErrors = calcStatus === CalcStatus.CALC_WITH_ERR || ((severeFolder || errordetail !== null) && (calcStatus !== CalcStatus.IN_PROCESS && calcStatus !== null))
  const warningReviewInputs = severeFolder || errordetail !== null && calcStatus === null
  const calculated = calcStatus === CalcStatus.CALCULATED
  const inProgress = calcStatus === CalcStatus.IN_PROCESS

  if (calculatedWithErrors) {
    calcStateComponent = (
      <PropertyCalcState_s calcWarnings id="propertyCalcState">
        <WithTip position="bottom">
          <span>Calc Property</span>
          <a onClick={() => startCalc()}>
            { hintsVisible && <ShortcutHint_s className="header-hint" visible={hintsVisible}>C</ShortcutHint_s> }
            <CalcIcon_s calcWarnings={true} src={CalcIcon}  id="calcIcon" />
          </a>
        </WithTip>
        <div>
          <PropertyCalcStateIcon_s calcWarnings>
            Calculated with Warnings
            { hintsVisible && <ShortcutHint_s className="header-hint" visible={hintsVisible}>W</ShortcutHint_s> }
          </PropertyCalcStateIcon_s>
        </div>
        <CalcValidationIcon_s calcWarnings>
          <WithTip position="bottom">
            <span>View Validation Issues</span>
            <a onClick={() => showValidationErrors()}>
              <CalcIcon_s calcWarnings={true} src={ValidationIcon}  id="validationIcon" />
            </a>
          </WithTip>
        </CalcValidationIcon_s>
      </PropertyCalcState_s>
    )
  } else if (warningReviewInputs) {
    calcStateComponent = (
      <WithTip position="bottom">
        <span>View Validation Issues</span>
        <PropertyCalcState_s notCalcError={true} id="propertyCalcState">
          <WithTip position="bottom">
            <span>Calc Property</span>
            <a onClick={() => startCalc()}>
              { hintsVisible && <ShortcutHint_s className="header-hint" visible={hintsVisible}>C</ShortcutHint_s> }
              <CalcIcon_s notCalcError={true} src={CalcIcon}  id="calcIcon" />
            </a>
          </WithTip>
          <div>
            <PropertyCalcStateIcon_s notCalcError={true}>
              Warning: Review Inputs
              { hintsVisible && <ShortcutHint_s className="header-hint" visible={hintsVisible}>W</ShortcutHint_s> }
            </PropertyCalcStateIcon_s>
          </div>
          <CalcValidationIcon_s notCalcError={true}>
            <WithTip position="bottom">
              <span>View Validation Issues</span>
              <a onClick={() => showValidationErrors()}>
              <CalcIcon_s calcDanger={true} src={ValidationIcon}  id="validationIcon" />
              </a>
            </WithTip>
          </CalcValidationIcon_s>
        </PropertyCalcState_s>
      </WithTip>
    )
  } else if (calculated) {
    calcStateComponent = <PropertyCalcState_s><PropertyCalcStateIcon_s calculated>Calculated</PropertyCalcStateIcon_s></PropertyCalcState_s>
  } else if (inProgress) {
    let progressBarLength = {
      width: `${(105 * (percentComplete / 100))}px`
    }
    calcStateComponent = (
      <WithTip position="bottom">
        <span>{`${percentComplete}% done`}</span>
        <PropertyCalcState_s inProgress id="propertyCalcState">
          <div onClick={() => cancelCalc()}>
            <CalcIcon_s inProgress={true} src={CalcIcon} />
            <CalcCancel_s inProgress src={CloseIcon} />
          </div>
          <div>
            <PropertyCalcStateIcon_s inProgress>
              Calc in Process
            </PropertyCalcStateIcon_s>
            <CalcProgressBar_s style={progressBarLength}></CalcProgressBar_s>
          </div>
        </PropertyCalcState_s>
      </WithTip>
    )
  }

  if(!currentPropId || currentPropId === "null") {
    calcStateComponent = null
  }

  return (
    <HeaderIcon_s>
      {calcStatus !== CalcStatus.IN_PROCESS && // if calc in process, use the calcStatus component defined above
        calcStatus !== CalcStatus.CALC_WITH_ERR && // if calc has errors, use the calcStatus component defined above
        !errordetail && // if calc has errors, use the calcStatus component defined above
        !severeFolder &&
        <WithTip position="bottom">
          <span>Calc Property</span>
          <a id="calcReport" onClick={()=> calc()}>
            { hintsVisible && <ShortcutHint_s className="header-hint" visible={hintsVisible}>C</ShortcutHint_s> }
            <Isvg src={CalcIcon} id="calcIcon" />
          </a>
        </WithTip>
      }
      { calcStateComponent }
      { calcConfirmation }
    </HeaderIcon_s>
  )
}

export default CalcButton
